<template>
  <div style="display:contents">
    <!-- 有消息时显示标题栏，无消息时隐藏 -->
    <div v-show="hasMessages" class="agent-session-head" data-agent-session-head>
      <div class="agent-session-head-main">
        <!-- 左侧：平铺元素，无外框容器 -->
        <div class="agent-head-left">
          <span class="agent-session-title" data-agent-thread-title :title="headerTitle">{{ headerTitle }}</span>
          <!-- 已停用 agent: Wechat/记忆/个性/Token/深度 全部隐藏(查不了/改不了) -->
          <WechatPill v-if="showWechatPill && !isCurrentAgentDisabled" :agent-id="currentAgentId" />
          <span v-if="!isCurrentAgentDisabled" class="agent-head-badge badge-memory" @click.stop="openInsight('memory')" style="cursor:pointer">记忆</span>
          <span v-if="!isCurrentAgentDisabled" class="agent-head-badge badge-soul" @click.stop="openInsight('soul')" style="cursor:pointer">个性</span>
          <span v-if="tokenDisplay && !isCurrentAgentDisabled" class="agent-head-token-pill" :class="tokenPillClass">{{ tokenDisplay }}</span>
          <span v-if="!isCurrentAgentDisabled" class="agent-head-depth-pill" :class="depthPillClass" @click.stop="openDepthPicker" style="cursor:pointer">
            <span class="agent-head-depth-dot" :style="{ background: depthDotColor }"></span>
            <span>{{ depthLabel }}</span>
          </span>
          <!-- 定时任务挪到左侧 inline-pill,跟记忆/个性/Token/深度并列(已停用 agent 也保留,可查看历史) -->
          <span class="agent-session-inline-pill" @click.stop="openDetail">定时任务</span>
        </div>
        <!-- 右侧：操作按钮 (已停用 agent: 新建任务隐藏) -->
        <div class="agent-session-title-actions">
          <button v-if="!isCurrentAgentDisabled" class="agent-session-action-btn" type="button" @click="openTopicDialog">新建任务</button>
          <button class="chat-search-btn" type="button" aria-label="搜索聊天记录" @click.stop="toggleChatSearch">
            <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.8"/><path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          </button>
          <button class="workspace-collapse-btn" type="button" aria-label="折叠工作区" @click.stop="toggleWorkspace">
            <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" stroke-width="1.8"/><line x1="15" y1="3" x2="15" y2="21" stroke="currentColor" stroke-width="1.8"/></svg>
          </button>
        </div>
      </div>
    </div>

    <!-- 置顶消息 bar -->
    <PinnedMessageBar v-if="hasMessages" :pinned-key="pinnedKey" @open-panel="openPinnedPanel" @dismiss="dismissPinnedBar" />

    <!-- 无消息时直接渲染 AgentStateEmpty，包裹容器复用 main-empty 布局 -->
    <div v-show="!hasMessages" class="agent-processing-empty-wrap">
      <AgentStateEmpty v-if="!hasMessages" @message-sent="onMessageSent" />
    </div>

    <div v-show="hasMessages" class="processing-canvas telegram-chat-body" id="processingCanvasFlow" ref="canvas"
      :class="{ 'tg-drag-over': isDragOver }"
      @dragenter.prevent="isDragOver = true"
      @dragover.prevent
      @dragleave="onDragLeave"
      @drop.prevent="handleDrop"
    >
      <!-- Attachment overlay -->
      <div v-if="pendingAttachments.length" class="tg-attach-overlay">
        <div class="tg-attach-panel">
          <div class="tg-attach-head">
            <span class="tg-attach-title">发送文件
              <span v-if="pendingAttachments.length > 1" class="tg-attach-count">{{ pendingAttachments.length }} 个</span>
            </span>
            <div class="tg-attach-head-actions">
              <button class="tg-attach-add-btn" type="button" @click="triggerFileInput">
                <svg viewBox="0 0 24 24" fill="none" width="18" height="18"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              </button>
              <button class="tg-attach-close" type="button" @click="cancelAttachment">
                <svg viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              </button>
            </div>
          </div>
          <div class="tg-attach-grid">
            <div v-for="(att, i) in pendingAttachments" :key="i" class="tg-attach-grid-item">
              <img v-if="att.kind === 'image'" :src="att.localUrl" class="tg-attach-grid-img" />
              <video v-else-if="att.kind === 'video'" :src="att.localUrl" class="tg-attach-grid-video" muted playsinline @loadedmetadata="e => { e.target.currentTime = 0.1 }"></video>
              <div v-else class="tg-attach-grid-doc">
                <div class="tg-attach-grid-doc-icon">{{ fileKindLabel(att) }}</div>
                <div class="tg-attach-grid-doc-name">{{ att.fileName }}</div>
                <div class="tg-attach-grid-doc-size">{{ att.fileSizeStr }}</div>
              </div>
              <div v-if="attachUploading" class="tg-attach-progress-overlay">
                <div class="tg-attach-progress-ring">
                  <svg viewBox="0 0 36 36" width="36" height="36">
                    <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="3"/>
                    <circle cx="18" cy="18" r="14" fill="none" stroke="#fff" stroke-width="3"
                      stroke-linecap="round"
                      :stroke-dasharray="87.96"
                      :stroke-dashoffset="87.96 * (1 - (uploadProgress[i] || 0) / 100)"
                      transform="rotate(-90 18 18)"
                    />
                  </svg>
                  <span class="tg-attach-progress-pct">{{ uploadProgress[i] || 0 }}%</span>
                </div>
              </div>
              <button v-if="!attachUploading" class="tg-attach-grid-remove" type="button" @click="removeAttachment(i)">
                <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
              </button>
            </div>
          </div>
          <div class="tg-attach-caption-row">
            <textarea class="tg-attach-caption" placeholder="添加说明（可选）..."
              v-model="attachCaption"
              @keydown.stop="onCaptionKeydown"
              @input="autoCaptionResize"
              rows="1" ref="captionInput"
              :disabled="attachUploading"
            ></textarea>
            <button class="tg-attach-send-btn" :class="{ uploading: attachUploading }" type="button" @click="sendAttachment" :disabled="attachUploading">
              <template v-if="attachUploading">
                <svg viewBox="0 0 36 36" width="22" height="22">
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="3"/>
                  <circle cx="18" cy="18" r="14" fill="none" stroke="#fff" stroke-width="3"
                    stroke-linecap="round"
                    :stroke-dasharray="87.96"
                    :stroke-dashoffset="87.96 * (1 - totalUploadProgress / 100)"
                    transform="rotate(-90 18 18)"
                  />
                </svg>
              </template>
              <svg v-else viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M22 2L15 22l-4-9-9-4 18-7z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Messages -->
      <div class="telegram-chat-flow">
        <div v-if="chatLoading && (!apiMessages || apiMessages.length === 0)" class="telegram-empty-messages"><div class="tg-chat-spinner"></div>加载中…</div>
        <div v-if="isLoadingMore && !chatLoading" class="chat-load-more-bar"><div class="tg-chat-spinner"></div>加载中…</div>
        <template v-for="(msg, idx) in apiMessages">
          <!-- Day chip -->
          <div v-if="msg.kind === 'day'" :key="'day-' + (msg.id || idx)" class="telegram-day-chip">{{ msg.text }}</div>
          <!-- system notification -->
          <div v-else-if="msg.kind === 'system'" :key="'sys-' + (msg.id || idx)" :id="msg.id ? 'msg-' + msg.id : null" class="telegram-system-row"><div class="telegram-system-msg" :class="sysClass(msg.text)">
            <svg v-if="sysClass(msg.text) === 'sys-join'" class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
            <svg v-else-if="sysClass(msg.text) === 'sys-leave'" class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" y1="11" x2="23" y2="11"/></svg>
            <svg v-else-if="sysClass(msg.text) === 'sys-agent'" class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M12 2a4 4 0 014 4v5H8V6a4 4 0 014-4z"/><line x1="8" y1="16" x2="8" y2="16.01"/><line x1="16" y1="16" x2="16" y2="16.01"/></svg>
            <svg v-else-if="sysClass(msg.text) === 'sys-topic'" class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
            <svg v-else-if="sysClass(msg.text) === 'sys-topic-close'" class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            <svg v-else class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
            <span v-html="sysHighlight(msg.text)"></span>
          </div></div>
          <!-- typing row（agent 思考中三个点；name + 黄色 [Agent] 徽章必现） -->
          <div v-else-if="msg.typing" :key="'typing-' + idx" class="telegram-row left">
            <div class="telegram-message-avatar" :style="msgAvatarStyle(msg)"><img v-if="msg.avatarImage" :src="msg.avatarImage" style="width:100%;height:100%;object-fit:cover;border-radius:inherit;" /><template v-else>{{ msgAvatarText(msg) }}</template></div>
            <div class="telegram-message-stack">
              <div class="telegram-bubble incoming">
                <div class="telegram-name-line">
                  <div v-if="msg.name" class="telegram-group-name">{{ msg.name }}</div>
                  <span class="telegram-agent-badge">{{ msg.agentTag || 'Agent' }}</span>
                </div>
                <div class="telegram-typing"><span></span><span></span><span></span></div>
              </div>
            </div>
          </div>

          <!-- regular message -->
          <div v-else :key="'msg-' + idx" :id="msg.id ? 'msg-' + msg.id : null" class="telegram-row is-replyable" :class="[msg.side === 'right' ? 'right' : 'left', { 'multi-select-mode': multiSelectMode }]" @click="multiSelectMode ? toggleMultiSelectAgent(msg) : null">
            <div v-if="multiSelectMode" class="multi-select-check" :class="{ checked: isMultiSelectedAgent(msg.id) }"></div>
            <div class="telegram-message-avatar" :class="{ self: msg.side === 'right' }" :style="msgAvatarStyle(msg)">{{ msgAvatarText(msg) }}</div>
            <div class="telegram-message-stack">
              <div class="telegram-bubble" :class="msg.side === 'right' ? 'outgoing' : 'incoming'" @contextmenu.prevent="openBubbleCopyMenu($event, msg)">
                <!-- Upload progress overlay -->
                <div v-if="msg.status === 'sending' && msg.progress !== undefined" class="bubble-upload-progress">
                  <svg viewBox="0 0 40 40" width="40" height="40">
                    <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="3.5"/>
                    <circle cx="20" cy="20" r="16" fill="none" stroke="#fff" stroke-width="3.5"
                      stroke-linecap="round"
                      :stroke-dasharray="100.53"
                      :stroke-dashoffset="100.53 * (1 - (msg.progress || 0) / 100)"
                      transform="rotate(-90 20 20)"
                      style="transition: stroke-dashoffset 0.3s ease"
                    />
                  </svg>
                  <span class="bubble-upload-pct">{{ msg.progress || 0 }}%</span>
                </div>
                <div v-if="msg.name && msg.side !== 'right'" class="telegram-name-line">
                  <div class="telegram-group-name">{{ msg.name }}</div>
                  <span v-if="msg.agentTag" class="telegram-agent-badge">{{ msg.agentTag }}</span>
                </div>
                <!-- Quote card -->
                <button v-if="msg.replyTo" class="telegram-quote-card" type="button" @click.stop="jumpToQuote(msg)">
                  <div class="telegram-quote-main">
                    <div class="telegram-quote-name">{{ msg.replyTo.name }}<span v-if="msg.replyTo.agentTag" class="telegram-agent-badge">{{ msg.replyTo.agentTag }}</span></div>
                    <div v-if="msg.replyTo.files && msg.replyTo.files.length > 1" class="telegram-quote-file"><span class="telegram-quote-file-icon">📎</span><span v-if="msg.replyTo.text" class="telegram-quote-file-name">{{ msg.replyTo.text }}</span><span class="telegram-quote-file-count">{{ msg.replyTo.files.length }} 个文件</span></div>
                    <div v-else-if="msg.replyTo.files && msg.replyTo.files.length === 1" class="telegram-quote-file"><span class="telegram-quote-file-icon">📎</span><span class="telegram-quote-file-name">{{ msg.replyTo.text || msg.replyTo.files[0].name || '文件' }}</span></div>
                    <div v-else class="telegram-quote-text">{{ msg.replyTo.text || '[消息]' }}</div>
                  </div>
                  <img v-if="msg.replyTo.files && msg.replyTo.files.length && msg.replyTo.files[0].type === 'image'" class="telegram-quote-thumb" :src="msg.replyTo.files[0].url" />
                </button>
                <!-- Audio -->
                <template v-if="msg.kind === 'audio'">
                  <div class="tg-audio-player" :class="{ 'tg-audio-right': msg.side === 'right' }">
                    <audio :ref="'audio_' + msg.id" :src="msg.fileUrl" preload="none"
                      @timeupdate="onAudioTimeUpdate(msg.id, $event)"
                      @loadedmetadata="onAudioMeta(msg.id, $event)"
                      @ended="onAudioEnded(msg.id)"></audio>
                    <button class="tg-audio-btn" type="button" @click="toggleAudio(msg.id)">
                      <svg v-if="audioState[msg.id] && audioState[msg.id].playing" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                      <svg v-else viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M8 5v14l11-7z"/></svg>
                    </button>
                    <div class="tg-audio-body">
                      <div class="tg-audio-progress-wrap" @click="seekAudio(msg.id, $event)">
                        <div class="tg-audio-track">
                          <div class="tg-audio-fill" :style="{ width: audioProgress(msg.id) + '%' }"></div>
                          <div class="tg-audio-bars">
                            <span v-for="n in 28" :key="n" class="tg-audio-bar" :class="{ active: (n / 28 * 100) <= audioProgress(msg.id) }"></span>
                          </div>
                        </div>
                      </div>
                      <div class="tg-audio-meta-row">
                        <span class="tg-audio-time">{{ audioDisplayTime(msg.id) }}</span>
                        <button class="tg-audio-open-ws-btn" type="button" @click.stop="openInWorkspace(msg)">
                          <span class="tg-audio-open-ws-name">{{ msg.fileName || 'Audio' }}</span>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-if="msg.text" class="telegram-bubble-text md-content" style="margin-top:6px" v-html="msg.side !== 'right' ? renderMd(msg.text) : renderUserMsg(msg.text, msg)"></div>
                </template>
                <!-- Multi-file -->
                <div v-else-if="msg.files && msg.files.length > 1" class="tg-multi-file-wrap">
                  <div class="tg-multi-file-list">
                    <div v-for="(f, fi) in msg.files" :key="fi" class="tg-multi-file-item" @click="openFileItem(f, msg)" @contextmenu.prevent.stop="openCopyMenu($event, msg, isImageFile(f) ? f.url : '', isImageFile(f) ? '' : f.url, isImageFile(f) ? '' : (f.name || '文件'))">
                      <div v-if="f.type === 'image' || (f.mimeType && f.mimeType.startsWith('image/')) || ['jpg','jpeg','png','gif','webp','svg','bmp'].includes((f.name||'').split('.').pop().toLowerCase())" class="tg-mf-img-wrap">
                        <img :src="f.url" class="tg-mf-img" />
                      </div>
                      <div v-else-if="f.type === 'video' || (f.mimeType && f.mimeType.startsWith('video/')) || ['mp4','webm','mkv','avi','mov'].includes((f.name||'').split('.').pop().toLowerCase())" class="tg-mf-img-wrap tg-mf-video-thumb">
                        <video :src="f.url" class="tg-mf-img" preload="metadata" muted playsinline @loadedmetadata="e => { e.target.currentTime = 0.1 }"></video>
                        <span class="tg-mf-play">▶</span>
                      </div>
                      <div v-else-if="f.type === 'audio' || (f.mimeType && f.mimeType.startsWith('audio/')) || ['mp3','wav','ogg','aac','flac','wma','m4a'].includes((f.name||'').split('.').pop().toLowerCase())" class="tg-mf-doc-icon" style="background:#35B58A">♪</div>
                      <div v-else class="tg-mf-doc-icon">{{ fileExtLabel(f.name || '') }}</div>
                      <div class="tg-mf-info">
                        <span class="tg-mf-name">{{ f.name || '文件' }}</span>
                        <span class="tg-mf-size">{{ formatSize(f.size) }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-if="msg.text" class="telegram-bubble-text md-content" style="margin-top:6px" v-html="msg.side !== 'right' ? renderMd(msg.text) : renderUserMsg(msg.text, msg)"></div>
                </div>
                <!-- Video -->
                <div v-else-if="msg.kind === 'video'" class="telegram-video-wrap" @click="openInWorkspace(msg)">
                  <div class="telegram-video-thumb">
                    <video :src="msg.fileUrl" class="telegram-bubble-video" preload="metadata" muted playsinline @loadedmetadata="e => { e.target.currentTime = 0.1 }"></video>
                    <div class="telegram-video-play">▶</div>
                  </div>
                  <div v-if="msg.text" class="telegram-bubble-text md-content" style="margin-top:6px" v-html="msg.side !== 'right' ? renderMd(msg.text) : renderUserMsg(msg.text, msg)"></div>
                </div>
                <!-- Image -->
                <div v-else-if="msg.kind === 'image'" class="telegram-bubble-image-wrap">
                  <img :src="msg.imageUrl" class="telegram-bubble-image" :style="imgPlaceholderStyle(msg)" @load="onImageLoad" @click="openInWorkspace(msg)" />
                  <div v-if="msg.text" class="telegram-bubble-text md-content" v-html="msg.side !== 'right' ? renderMd(msg.text) : renderUserMsg(msg.text, msg)"></div>
                </div>
                <!-- Doc/File -->
                <div v-else-if="msg.kind === 'document' || msg.kind === 'file' || msg.fileUrl || (msg.files && msg.files.length)" class="telegram-doc-wrap">
                  <button class="telegram-file-card" type="button" @click="openInWorkspace(msg)">
                    <div class="telegram-file-top">
                      <div class="telegram-file-icon" :class="fileIconClass(msg)">{{ fileIconLabel(msg) }}</div>
                      <div class="telegram-file-copy">
                        <div class="telegram-file-title">{{ msg.fileName }}</div>
                        <div class="telegram-file-meta">{{ msg.fileSizeStr }}</div>
                      </div>
                      <svg class="telegram-file-open-icon" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7v10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </div>
                  </button>
                  <div v-if="msg.text" class="telegram-file-caption telegram-bubble-text md-content" v-html="msg.side !== 'right' ? renderMd(msg.text) : renderUserMsg(msg.text, msg)"></div>
                </div>
                <!-- Text -->
                <template v-else>
                  <div v-if="msg.side !== 'right'" class="telegram-bubble-text md-content" v-html="renderMd(msg.text)"></div>
                  <div v-else class="telegram-bubble-text" v-html="renderUserMsg(msg.text, msg)"></div>
                </template>
                <div class="telegram-bubble-time">
                  {{ msg.time }}
                  <span v-if="msg.status === 'failed'" class="msg-failed-indicator" @click.stop="retryMessage(msg)" title="点击重试">⚠️ 发送失败 · 重试</span>
                  <span v-else-if="msg.status === 'sending'" class="msg-sending-indicator">发送中...</span>
                  <span v-else-if="msg.side === 'right'" class="msg-read-status" :class="msg.readCount > 0 ? 'read' : 'unread'">
                    <svg v-if="msg.readCount > 0" viewBox="0 0 18 12" fill="none"><path d="M1 6l4 4L13 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 6l4 4L18 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    <svg v-else viewBox="0 0 14 12" fill="none"><path d="M1 6l4 4L13 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Streaming: 只显示 typing 状态，不显示流式内容，等 message.new 显示完整结果 -->
        <div v-if="streamingMessage" class="telegram-row left streaming-row">
          <div class="telegram-message-avatar" :style="{ background: streamingMessage.avatarUrl ? 'transparent' : _avatarSolid(streamingMessage.avatarColorRgb) }">
            <img v-if="streamingMessage.avatarUrl" :src="streamingMessage.avatarUrl" style="width:100%;height:100%;object-fit:cover;border-radius:inherit;" />
            <template v-else>{{ (streamingMessage.agentName || 'A').slice(0,1) }}</template>
          </div>
          <div class="telegram-message-stack">
            <div class="telegram-bubble incoming streaming-bubble">
              <div v-if="streamingMessage.agentName" class="telegram-name-line">
                <div class="telegram-group-name">{{ streamingMessage.agentName }}</div>
                <span class="telegram-agent-badge">Agent</span>
              </div>
              <template v-if="streamingMessage.content">
                <div class="telegram-bubble-text md-content" v-html="renderMd(streamingVisibleText)"></div>
                <span class="streaming-cursor"></span>
              </template>
              <div v-else class="telegram-typing"><span></span><span></span><span></span></div>
            </div>
          </div>
        </div>

        <!-- Typing indicator（agent 思考中三个点；name + 黄色 [Agent] 徽章必现） -->
        <div v-else-if="agentTyping" class="telegram-row left">
          <div class="telegram-message-avatar" :style="{ background: agentTyping.avatarUrl ? 'transparent' : _avatarSolid(agentTyping.avatarColorRgb) }"><img v-if="agentTyping.avatarUrl" :src="agentTyping.avatarUrl" style="width:100%;height:100%;object-fit:cover;border-radius:inherit;" /><template v-else>{{ (agentTyping.agentName || 'A').slice(0,1) }}</template></div>
          <div class="telegram-message-stack">
            <div class="telegram-bubble incoming">
              <div class="telegram-name-line">
                <div v-if="agentTyping.agentName" class="telegram-group-name">{{ agentTyping.agentName }}</div>
                <span class="telegram-agent-badge">Agent</span>
              </div>
              <div class="telegram-typing"><span></span><span></span><span></span></div>
            </div>
          </div>
        </div>
      </div>
      <!-- 跳转到最新消息 -->
      <button v-if="showScrollBtn" class="scroll-to-bottom-btn" type="button" @click="scrollToLatest">
        <svg viewBox="0 0 24 24" fill="none" width="24" height="24"><path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>

    <!-- 引用回复草稿 -->
    <div v-if="pendingReply && hasMessages" class="telegram-reply-draft" style="margin-top:10px;">
      <div class="telegram-reply-copy">
        <div class="telegram-reply-name">{{ pendingReply.name }}</div>
        <div v-if="pendingReply.kind === 'image'" class="telegram-reply-file"><span class="telegram-reply-file-icon">🖼️</span><span>{{ pendingReply.text || pendingReply.fileName || '图片' }}</span></div>
        <div v-else-if="pendingReply.kind === 'video'" class="telegram-reply-file"><span class="telegram-reply-file-icon">🎬</span><span>{{ pendingReply.text || pendingReply.fileName || '视频' }}</span></div>
        <div v-else-if="pendingReply.kind === 'audio'" class="telegram-reply-file"><span class="telegram-reply-file-icon">🎵</span><span>{{ pendingReply.text || pendingReply.fileName || '语音' }}</span></div>
        <div v-else-if="pendingReply.kind === 'document' || pendingReply.kind === 'file'" class="telegram-reply-file"><span class="telegram-reply-file-icon">📄</span><span>{{ pendingReply.text || pendingReply.fileName || '文件' }}</span></div>
        <div v-else class="telegram-reply-text">{{ pendingReply.text || '[消息]' }}</div>
      </div>
      <button class="telegram-reply-close" type="button" @click="clearReply">
        <svg viewBox="0 0 24 24" fill="none" width="18" height="18"><path d="M8 8l8 8M16 8l-8 8" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"></path></svg>
      </button>
    </div>

    <div v-show="hasMessages && !multiSelectMode" class="processing-footer">
      <!-- Agent 已停用：替代整个 composer 显示禁用提示，技能/命令/模型/附件/输入/发送都不渲染 -->
      <div v-if="isCurrentAgentDisabled" class="processing-composer processing-composer-disabled">
        <svg viewBox="0 0 24 24" fill="none" style="width:16px;height:16px;flex-shrink:0"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/><path d="M5.64 5.64l12.72 12.72" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
        <span>该 Agent 已停用，无法发送消息。仅可查看历史会话与交付物。</span>
      </div>
      <div v-else class="processing-composer">
        <div class="processing-editor" style="position:relative;">
          <div v-if="voiceActive" class="voice-recording-panel">
            <template v-if="voiceError">
              <div class="voice-recording-error">{{ voiceError }}</div>
              <button class="voice-recording-stop" type="button" @click="voiceActive = false; voiceError = ''">关闭</button>
            </template>
            <template v-else>
              <div class="voice-recording-indicator">
                <span class="voice-recording-dot"></span>
                <span class="voice-recording-label">正在聆听...</span>
              </div>
              <div v-if="voiceText" class="voice-recording-result">{{ voiceText }}</div>
              <button class="voice-recording-stop" type="button" @click="toggleVoice">停止录入</button>
            </template>
          </div>
          <textarea v-else id="processingComposerInput"
            :placeholder="cloudActionBusy || ('发送消息给 ' + currentSessionName)"
            :value="cloudActionBusy ? '' : inputValue"
            :disabled="!!cloudActionBusy"
            :style="cloudActionBusy ? 'opacity:0.5;cursor:not-allowed;background:rgba(228,233,242,0.3)' : ''"
            @input="onInput" @keydown="onKeydown" ref="textarea"
          ></textarea>
        </div>
        <div class="composer-feedback-row" data-skill-feedback="processing" :hidden="selectedSkills.length === 0 && !selectedCommand">
          <span v-if="selectedCommand" class="composer-cmd-chip">
            <span class="composer-cmd-chip-text">{{ selectedCommand }}</span>
            <button class="composer-cmd-chip-remove" type="button" @click="$store.commit('SET_SESSION_COMMAND', null)">×</button>
          </span>
          <span v-for="skill in selectedSkills" :key="skill.id" class="composer-skill-chip">
            <span class="composer-skill-chip-icon">
              <img v-if="isUrlIcon(skill.icon)" :src="skill.icon" alt="" @error="onIconError" />
              <span v-else>{{ skill.icon }}</span>
            </span>
            <span>{{ skill.name }}</span>
            <button class="composer-skill-chip-remove" type="button" @click="removeSkill(skill.id)">×</button>
          </span>
        </div>
        <div class="processing-toolbar">
          <div class="processing-left">
            <button class="tool-btn" type="button" @click="triggerFileInput">
              <svg viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
              <span class="tool-btn-label">上传</span>
            </button>
            <input type="file" ref="fileInput" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.zip,.rar,.7z" style="display:none" multiple @change="handleFileSelect" />
            <button class="tool-btn" :class="{ 'skill-selected': cmdPickerOpen || !!selectedCommand, 'tool-btn-disabled': selectedSkills.length > 0 }" type="button" @click.stop="openCmdPicker" :title="selectedSkills.length > 0 ? '使用技能时不可选择命令' : '命令'">
              <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M8 9l3-3 3 3M8 15l3 3 3-3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <span class="tool-btn-label">命令</span>
              <span class="composer-model-btn-caret">▾</span>
            </button>
            <button class="tool-btn" :class="{ 'skill-selected': selectedSkills.length > 0, 'tool-btn-disabled': !!selectedCommand || isCommandMode }" :disabled="isCommandMode" type="button" data-skill-trigger="processing" @click="openSkillPicker($event)" :title="isCommandMode ? '命令模式下不可使用技能' : (selectedCommand ? '使用命令时不可选择技能' : '使用技能')">
              <svg viewBox="0 0 24 24" fill="none"><path d="M12 3 A9 9 0 0 1 12 21 A9 9 0 0 1 12 3 Z M14.5 7.5 L9.5 16.5" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path></svg>
              <span class="tool-btn-label">技能</span>
              <span v-if="selectedSkills.length > 0" class="tool-btn-clear" @click.stop="clearAllSkills"><svg viewBox="0 0 24 24" fill="none"><path d="M8 8l8 8M16 8l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg></span>
            </button>
            <button class="tool-btn composer-model-btn" :class="{ 'tool-btn-disabled': isCommandMode }" :disabled="isCommandMode" type="button" data-model-trigger="processing" @click="!isCommandMode && openModelPicker($event)" :title="isCommandMode ? '命令模式下不可切换模型' : ''">
              <span class="tool-btn-label tool-btn-model-label">{{ selectedModel }}</span>
              <span class="composer-model-btn-caret">▾</span>
            </button>
          </div>
          <div class="processing-right">
            <!-- <button class="processing-icon-btn" :class="{ 'voice-active': voiceActive }" title="语音输入" @click="toggleVoice"><svg viewBox="0 0 24 24" fill="none"><path d="M12 3a3 3 0 013 3v6a3 3 0 11-6 0V6a3 3 0 013-3zM19 11a7 7 0 01-14 0M12 18v3M8 21h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg></button> -->
            <button class="processing-send-btn" title="发送" @click="send">
              <svg viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path><path d="M22 2L15 22l-4-9-9-4 18-7z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- 多选底部工具栏（canvas 外面，固定在底部） -->
    <div v-if="multiSelectMode && hasMessages" class="multi-select-bar">
      <button type="button" class="multi-select-action" @click="multiForwardEachAgent" :disabled="!multiSelectedMessages.length">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 14l5-5-5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 9H9a7 7 0 0 0-7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        逐条转发
      </button>
      <button type="button" class="multi-select-action" @click="multiForwardMergeAgent" :disabled="!multiSelectedMessages.length">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/></svg>
        合并转发
      </button>
      <button type="button" class="multi-select-cancel" @click="exitMultiSelectAgent">× 取消</button>
    </div>
    <ModelPickerPopover />
    <ThinkingDepthPopover />
    <SkillPickerPopover />
    <CommandPickerPopover
      :open="cmdPickerOpen"
      :trigger-rect="cmdTriggerRect"
      @select="insertCommand"
      @close="cmdPickerOpen = false"
    />
    <!-- 右键菜单 -->
    <div v-if="copyMenu.open" class="agent-copy-menu" :style="{ left: copyMenu.x + 'px', top: copyMenu.y + 'px' }">
      <button class="agent-copy-menu-item" type="button" @click="doReply">
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 10L4 15l5 5"/><path d="M4 15h9a7 7 0 017 7"/></svg>
        引用回复
      </button>
      <button class="agent-copy-menu-item" type="button" @click="doForwardFromAgent">
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M15 14l5-5-5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 9H9a7 7 0 0 0-7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        转发
      </button>
      <button class="agent-copy-menu-item" type="button" @click="togglePinFromAgent">
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M12 2v8M9 4l3 3 3-3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 12h14M8 12v4a4 4 0 008 0v-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        {{ isCurrentMessagePinned ? '取消置顶' : '置顶消息' }}
      </button>
      <button class="agent-copy-menu-item" type="button" @click="enterMultiSelectAgent">
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M9 11l3 3L22 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        多选
      </button>
      <button class="agent-copy-menu-item" type="button" @click="doCopyText">
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" stroke-width="1.8"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" stroke-width="1.8"/></svg>
        复制文本
      </button>
      <button v-if="copyMenu.clickedImageUrl" class="agent-copy-menu-item" type="button" @click="doCopyImage">
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.8"/><circle cx="9" cy="9" r="2" stroke="currentColor" stroke-width="1.8"/><path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        复制图片
      </button>
      <button v-if="copyMenu.clickedFileUrl" class="agent-copy-menu-item" type="button" @click="doDownloadFile">
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M12 3v12m0 0l-4-4m4 4l4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 21h14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
        下载文件
      </button>
    </div>

    <!-- 置顶消息面板 -->
    <PinnedMessagesPanel
      :pinned-key="pinnedKey"
      :conversation-id="currentConversationId || ''"
      :topic-id="currentTopicKey || ''"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { api } from '../utils/api'
import { renderMarkdown, linkifyText } from '../utils/markdown'
import { solidFromColor } from '../utils/avatarColor'
import { isSpeechSupported, getSpeechUnsupportedReason, startSpeechRecognition, stopSpeechRecognition } from '../utils/speech'
import AgentStateEmpty from './AgentStateEmpty.vue'
import ModelPickerPopover from './ModelPickerPopover.vue'
import SkillPickerPopover from './SkillPickerPopover.vue'
import CommandPickerPopover from './CommandPickerPopover.vue'
import ThinkingDepthPopover from './ThinkingDepthPopover.vue'
import WechatPill from './WechatPill.vue'
import PinnedMessageBar from './PinnedMessageBar.vue'
import PinnedMessagesPanel from './PinnedMessagesPanel.vue'

function _getImageDimensions(url) {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => resolve({ width: 0, height: 0 })
    img.src = url
  })
}
function _preloadImage(url) {
  return new Promise(resolve => { const img = new Image(); img.onload = resolve; img.onerror = resolve; img.src = url })
}

export default {
  name: 'AgentStateProcessing',
  components: { AgentStateEmpty, ModelPickerPopover, SkillPickerPopover, CommandPickerPopover, ThinkingDepthPopover, WechatPill, PinnedMessageBar, PinnedMessagesPanel },
  data() {
    return {
      pendingAttachments: [],
      attachCaption: '',
      attachUploading: false,
      streamRenderedLen: 0,
      isDragOver: false,
      copyMenu: { open: false, x: 0, y: 0, text: '' },
      showScrollBtn: false,
      voiceActive: false,
      voiceText: '',
      voiceError: '',
      _voiceBaseText: '',
      uploadProgress: [],
      audioState: {},
      sentSessionKeys: new Set(),
      cmdPickerOpen: false,
      cmdTriggerRect: null,
    }
  },
  watch: {},
  computed: {
    ...mapState(['composerInputValues', 'currentSessionKey', 'sessionItems', 'currentTopicKey', 'sessionTopicPages', 'auth', 'agentInspectorProfiles', 'agentSoulOverrides', 'agentMemoryOverrides', 'thinkingDepth', 'chatLoading', 'multiSelectMode', 'multiSelectedMessages', 'cloudActionBusy', 'pinnedPanelOpen']),
    pinnedKey() {
      const cid = this.currentConversationId
      if (!cid) return ''
      return `${cid}::${this.currentTopicKey || 'default'}`
    },
    isCurrentMessagePinned() {
      const messageId = this.copyMenu && this.copyMenu.messageId
      if (!messageId) return false
      const list = this.$store.state.conversationPinnedMessages[this.pinnedKey] || []
      return list.some(p => p.messageId === messageId)
    },
    ...mapGetters(['selectedModel', 'selectedSkills', 'selectedCommand', 'currentConversationId', 'currentConversationMessages', 'currentAgentTyping', 'currentStreamingMessage', 'agentSoulText', 'agentMemoryText', 'currentTelegramPendingReply', 'currentSession']),
    // 1对1 Agent 已停用 → 禁用整个输入区(技能/命令/模型/附件/文本框/发送全部不可用)
    isCurrentAgentDisabled() {
      const s = this.currentSession
      return !!(s && s.type === 'agent' && s.myAgentStatus === 'disabled')
    },
    pendingReply() { return this.currentTelegramPendingReply },
    isLoadingMore() {
      const cid = this.currentConversationId
      return !!(cid && this.$store.state.conversationMessagesLoading[cid])
    },
    apiMessages() {
      const list = this.currentConversationMessages || []
      const result = []
      let lastDate = ''
      for (let i = 0; i < list.length; i++) {
        const msg = list[i]
        if (msg.createdAt) {
          const d = new Date(msg.createdAt)
          const dateKey = d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate()
          if (dateKey !== lastDate) {
            lastDate = dateKey
            const now = new Date()
            const today = now.getFullYear() + '-' + (now.getMonth() + 1) + '-' + now.getDate()
            const yd = new Date(now - 86400000)
            const yesterdayKey = yd.getFullYear() + '-' + (yd.getMonth() + 1) + '-' + yd.getDate()
            let label
            if (dateKey === today) label = '今天'
            else if (dateKey === yesterdayKey) label = '昨天'
            else if (d.getFullYear() === now.getFullYear()) label = (d.getMonth() + 1) + '月' + d.getDate() + '日'
            else label = d.getFullYear() + '年' + (d.getMonth() + 1) + '月' + d.getDate() + '日'
            result.push({ id: 'day-' + dateKey, kind: 'day', text: label })
          }
        }
        result.push(msg)
      }
      return result
    },
    hasMessages() {
      // 1. 本会话+话题 已发送过消息（强制标记）
      const sessionTopicKey = this.currentSessionKey + (this.currentTopicKey ? ':' + this.currentTopicKey : '')
      if (this.sentSessionKeys.has(sessionTopicKey)) return true
      // 2. 有话题时：按话题维度判断，不用整个 conversation 的消息
      if (this.currentTopicKey) {
        if (this.currentAgentTyping || this.currentStreamingMessage) return true
        if (this.currentConversationMessages && this.currentConversationMessages.length > 0) return true
        const item = this.sessionItems.find(s => s.key === this.currentSessionKey)
        const pageKey = item?.topicKey
        const page = pageKey ? this.sessionTopicPages[pageKey] : null
        const topic = page?.topics?.find(t => t.key === this.currentTopicKey)
        return !!(topic && topic.preview && topic.preview !== '暂无最新消息')
      }
      // 3. 无话题：全局会话判断
      if (this.sentSessionKeys.has(this.currentSessionKey)) return true
      if (this.currentConversationMessages && this.currentConversationMessages.length > 0) return true
      if (this.currentAgentTyping || this.currentStreamingMessage) return true
      const item = this.sessionItems.find(s => s.key === this.currentSessionKey)
      if (item && item.lastMessage) return true
      return false
    },
    agentTyping() { return this.currentAgentTyping },
    streamingMessage() { return this.currentStreamingMessage },
    streamingVisibleText() {
      const sm = this.currentStreamingMessage
      if (!sm || !sm.content) return ''
      return sm.content.substring(0, this.streamRenderedLen)
    },
    totalUploadProgress() {
      if (!this.uploadProgress.length) return 0
      return Math.round(this.uploadProgress.reduce((a, b) => a + b, 0) / this.uploadProgress.length)
    },
    composerContext() {
      const topicKey = this.currentTopicKey
      const sessionKey = this.currentSessionKey || 'unknown'
      return topicKey ? (sessionKey + '-processing-' + topicKey) : (sessionKey + '-processing')
    },
    inputValue() { return this.composerInputValues[this.composerContext] || '' },
    // 命令模式：输入框以 / 开头，或已选中命令
    isCommandMode() {
      return this.inputValue.startsWith('/') || !!this.selectedCommand
    },
    currentSessionName() {
      const s = this.sessionItems.find(i => i.key === this.currentSessionKey)
      return s ? s.name : 'Agent'
    },
    currentAgentId() {
      const convId = this.currentConversationId
      const agents = this.$store.state.conversationAgents[convId] || []
      return agents.length ? agents[0].id : null
    },
    showWechatPill() {
      if (!this.currentAgentId) return false
      const item = this.sessionItems.find(s => s.key === this.currentSessionKey)
      if (!item) return false
      const pageKey = item.topicKey
      const page = pageKey ? this.sessionTopicPages[pageKey] : null
      if (!page || !page.topics || page.topics.length <= 1) return true
      const currentTopic = page.topics.find(t => t.key === this.currentTopicKey)
      return !!(currentTopic && currentTopic.isDefault)
    },
    headerTitle() {
      const item = this.sessionItems.find(s => s.key === this.currentSessionKey)
      if (this.currentTopicKey && item) {
        const pageKey = item.topicKey
        const page = pageKey ? this.sessionTopicPages[pageKey] : null
        if (page && page.topics && page.topics.length > 1) {
          const topic = page.topics.find(t => t.key === this.currentTopicKey)
          if (topic) return topic.name || this.currentSessionName
        }
      }
      return this.currentSessionName
    },
    // 是否有记忆内容
    hasMemory() {
      const key = this.currentSessionKey
      const override = this.agentMemoryOverrides?.[key]
      if (override !== undefined) return !!override && override !== '暂无记忆记录。'
      const profile = this.agentInspectorProfiles?.[key]
      return !!(profile?.memory && profile.memory !== '暂无记忆记录。')
    },
    // 是否有个性内容
    hasSoul() {
      const key = this.currentSessionKey
      const override = this.agentSoulOverrides?.[key]
      if (override !== undefined) return !!override && override !== '暂无个性设置。'
      const profile = this.agentInspectorProfiles?.[key]
      return !!(profile?.soul && profile.soul !== '暂无个性设置。')
    },
    // Token 显示:多话题场景按 (convId, topicId) 复合 key 取 topic 自己的上下文,
    // 没有再降级 conversationId 单 key(WS 推送 / 单 topic 场景的兜底值)
    contextUsage() {
      const convId = this.currentConversationId
      if (!convId) return ''
      const map = this.$store.state.contextUsageDisplay || {}
      const topicId = this.currentTopicKey
      if (topicId && map[convId + '::' + topicId]) return map[convId + '::' + topicId]
      return map[convId] || ''
    },
    tokenDisplay() {
      return this.contextUsage || null
    },
    tokenPillClass() {
      const usage = this.contextUsage || ''
      const match = usage.match(/\((\d+)%\)/)
      const pct = match ? parseInt(match[1]) : 0
      return pct >= 70 ? 'pill-danger' : 'pill-low'
    },
    // 思考深度
    depthLabel() {
      const map = { off: '关闭', minimal: '极简', low: '快速', medium: '标准', high: '深度', xhigh: '极深', adaptive: '自适应' }
      return map[this.thinkingDepth] || '标准'
    },
    depthDotColor() {
      const map = { off: '#c0c8d4', minimal: '#a0aec0', low: '#68d391', medium: '#f6c343', high: '#4e9aff', xhigh: '#7c5cfc', adaptive: '#38c8c8' }
      return map[this.thinkingDepth] || '#f6c343'
    },
    depthPillClass() {
      const map = { off: 'depth-off', minimal: 'depth-minimal', low: 'depth-low', medium: 'depth-medium', high: 'depth-high', xhigh: 'depth-xhigh', adaptive: 'depth-adaptive' }
      return map[this.thinkingDepth] || 'depth-medium'
    },
    // 模型短名称 - 显示模型名，过长则截断
    modelShortLabel() {
      const m = this.selectedModel || ''
      if (!m) return '标准'
      if (m.toLowerCase().includes('fengbao') || m.toLowerCase().includes('auto')) return 'FengBao'
      return m.length > 12 ? m.slice(0, 12) + '…' : m
    },
    modelDotClass() {
      const m = (this.selectedModel || '').toLowerCase()
      if (m.includes('deepseek')) return 'dot-deepseek'
      if (m.includes('gpt') || m.includes('openai')) return 'dot-gpt'
      if (m.includes('glm')) return 'dot-glm'
      if (m.includes('kimi') || m.includes('moonshot')) return 'dot-kimi'
      if (m.includes('minimax')) return 'dot-minimax'
      return 'dot-default'
    },
    modelPillClass() {
      const m = (this.selectedModel || '').toLowerCase()
      if (m.includes('deepseek')) return 'model-deepseek'
      if (m.includes('gpt') || m.includes('openai')) return 'model-gpt'
      if (m.includes('glm')) return 'model-glm'
      if (m.includes('kimi') || m.includes('moonshot')) return 'model-kimi'
      if (m.includes('minimax')) return 'model-minimax'
      return ''
    }
  },
  methods: {
    ...mapMutations(['SET_COMPOSER_INPUT', 'CLEAR_SKILLS', 'SET_THINKING_DEPTH_PICKER_OPEN', 'SET_TELEGRAM_PENDING_REPLY']),
    ...mapActions(['sendConversationMessage', 'resendMessage']),
    openPinnedPanel() {
      this.$store.commit('SET_PINNED_PANEL_OPEN', true)
    },
    dismissPinnedBar() {
      const key = this.pinnedKey
      if (key) this.$store.commit('SET_PINNED_BAR_DISMISSED', { key, dismissed: true })
    },
    togglePinFromAgent() {
      const cm = this.copyMenu
      const messageId = cm && cm.messageId
      const conversationId = this.currentConversationId
      if (!messageId || !conversationId) {
        this.copyMenu.open = false
        return
      }
      const topicId = this.$store.state.currentTopicKey || undefined
      const wasPinned = this.isCurrentMessagePinned
      this.copyMenu.open = false
      if (wasPinned) {
        this.$store.dispatch('unpinMessage', { conversationId, topicId, messageId })
      } else {
        const message = {
          id: messageId,
          sender: { id: cm.senderId || '', name: cm.name || '' },
          body: { text: cm.text || '', files: cm.files || [] },
          createdAt: new Date().toISOString()
        }
        this.$store.dispatch('pinMessage', { conversationId, topicId, message })
      }
    },
    async openInsight(type) {
      const convId = this.currentConversationId
      if (convId) await this.$store.dispatch('fetchAgentPromptSections', convId)
      this.$store.dispatch('openAgentInsightModal', type)
    },
    _scrollKey() {
      return this.currentSessionKey + (this.currentTopicKey ? ':' + this.currentTopicKey : '')
    },
    _saveScrollPos() {
      const el = this.$refs.canvas
      if (!el) return
      const key = this._scrollKey()
      if (!key) return
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
      this.$store.state.chatScrollPositions[key] = { scrollTop: el.scrollTop, atBottom: distFromBottom <= 80 }
    },
    _restoreScrollPos() {
      const el = this.$refs.canvas
      if (!el) return
      const key = this._scrollKey()
      if (!key) return
      const saved = this.$store.state.chatScrollPositions[key]
      if (saved && !saved.atBottom) {
        this.$nextTick(() => { el.scrollTop = saved.scrollTop })
        this._userScrolledUp = true
      } else {
        // 没操作过或上次在底部 → 滚到最新消息
        this._userScrolledUp = false
        this.$nextTick(() => { el.scrollTop = el.scrollHeight })
      }
    },
    // 切会话/切话题时强制把流式显示长度对齐到当前 streaming content 的真实长度
    // 修复：之前切走再切回时，streamRenderedLen 是旧值、content 是新值，会从旧位置启动 typewriter，看起来像「从头打字」
    _syncStreamRendered() {
      if (this._typewriterTimer) {
        clearInterval(this._typewriterTimer)
        this._typewriterTimer = null
      }
      const sm = this.currentStreamingMessage
      this.streamRenderedLen = (sm && sm.content) ? sm.content.length : 0
      this._streamSwitchedAway = false
    },
    smoothScrollToBottom(force) {
      const el = this.$refs.canvas
      if (!el) return
      if (!force && this._userScrolledUp) return
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf)
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null
        el.scrollTop = el.scrollHeight
      })
    },
    onCanvasScroll() {
      const el = this.$refs.canvas
      if (!el) return
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
      this._userScrolledUp = distFromBottom > 80
      this.showScrollBtn = distFromBottom > 200
      this._saveScrollPos()
      const convId = this.currentConversationId
      // 跳转窗口下：滚到底部一屏内 → 触发 after 填中间
      if (convId &&
          this.$store.state.conversationHasMoreAfter[convId] &&
          !this.$store.state.conversationLoadingAfter[convId]) {
        if (distFromBottom < 800) {
          const topicId = this.$store.getters.safeCurrentTopicKey || undefined
          this.$store.dispatch('loadMessagesAfter', { conversationId: convId, topicId })
        }
      }
      // 滚到顶部加载更多历史消息
      if (el.scrollTop < 50) {
        if (!convId) return
        if (this.$store.state.conversationMessagesLoading[convId]) return
        if (!this.$store.state.conversationMessagesHasMore[convId]) return
        // 跳转窗口未填满时不触发 loadMore（避免 scrollIntoView 后 scrollTop≈0 误触发）
        if (this.$store.state.conversationHasMoreAfter[convId]) return
        if (this.$store.state.conversationLoadingAfter[convId]) return
        const oldHeight = el.scrollHeight
        this.$store.dispatch('fetchConversationMessages', {
          conversationId: convId,
          topicId: this.$store.getters.safeCurrentTopicKey || undefined,
          loadMore: true
        }).then(() => {
          this.$nextTick(() => { el.scrollTop = el.scrollHeight - oldHeight })
        })
      }
    },
    parseCmd(text) {
      if (!text) return { cmd: '', rest: '', prefix: '' }
      let work = text, prefix = ''
      const mentionMatch = work.match(/^((?:@\S+\s*)+)/)
      if (mentionMatch) {
        prefix = mentionMatch[1].trim()
        work = work.slice(mentionMatch[1].length)
      }
      if (!work.startsWith('/')) return { cmd: '', rest: '', prefix: '' }
      const match = work.match(/^(\/[\w-]+)(.*)$/)
      if (!match) return { cmd: '', rest: '', prefix: '' }
      return { cmd: match[1], rest: (match[2] || '').trim(), prefix }
    },
    onMessageSent() {
      const sessionTopicKey = this.currentSessionKey + (this.currentTopicKey ? ':' + this.currentTopicKey : '')
      this.sentSessionKeys.add(sessionTopicKey)
      this.sentSessionKeys.add(this.currentSessionKey)
    },
    onInput(e) {
      const val = e.target.value
      this.SET_COMPOSER_INPUT({ context: this.composerContext, value: val })
this.autoResize()
    },
    onKeydown(e) {
      // 输入法组合状态（如中文拼音输入中）不拦截
      if (e.isComposing || e.keyCode === 229) return
      const hk = this.$store.state.hotkeySettings || { send: { key: 'Enter' }, newline: { ctrl: true, key: 'Enter' } }
      if (this._matchHotkey(e, hk.send)) {
        e.preventDefault()
        this.send()
      } else if (this._matchHotkey(e, hk.newline)) {
        e.preventDefault()
        const ta = e.target
        ta.setRangeText('\n', ta.selectionStart, ta.selectionEnd, 'end')
        this.SET_COMPOSER_INPUT({ context: this.composerContext, value: ta.value })
        this.$nextTick(() => { this.autoResize(); ta.scrollTop = ta.scrollHeight })
      } else if (e.key === 'Enter') {
        e.preventDefault()
      }
    },
    _matchHotkey(e, h) {
      if (!h || !h.key) return false
      return e.key === h.key && !!e.ctrlKey === !!h.ctrl && !!e.shiftKey === !!h.shift && !!e.altKey === !!h.alt
    },
    send() {
      const val = this.inputValue.trim()
      const cmd = this.selectedCommand || ''
      if (!val && !cmd) return
      this._userScrolledUp = false
      const reply = this.pendingReply
      const replyToId = reply ? reply.messageId : undefined
      if (reply) this.clearReply()
      const convId = this.currentConversationId
      if (convId) this.sendConversationMessage({ conversationId: convId, text: val || '', replyToId, replyTo: reply ? { id: reply.messageId, name: reply.name || '', text: reply.text || '', files: reply.files || null } : undefined })
      this.SET_COMPOSER_INPUT({ context: this.composerContext, value: '' })
      this.$nextTick(() => this.autoResize())
    },
    focusTextarea() {
      const ta = this.$refs.textarea
      if (ta) ta.focus()
    },
    autoResize() {
      const ta = this.$refs.textarea
      if (!ta) return
      const lh = parseFloat(window.getComputedStyle(ta).lineHeight) || 24
      ta.style.height = 'auto'
      ta.style.height = Math.min(Math.max(ta.scrollHeight, lh * 2), lh * 10) + 'px'
    },
    // 气泡级右击：单图/单文件自动带上 clickedImageUrl/clickedFileUrl，多文件让 row 自己处理
    openBubbleCopyMenu(e, msg) {
      const isMultiFile = !!(msg.files && msg.files.length > 1)
      if (isMultiFile) return this.openCopyMenu(e, msg)
      const isImage = msg.kind === 'image' && !!msg.imageUrl
      const hasFile = !isImage && !!msg.fileUrl
      this.openCopyMenu(
        e, msg,
        isImage ? msg.imageUrl : '',
        hasFile ? msg.fileUrl : '',
        hasFile ? (msg.fileName || '文件') : ''
      )
    },
    openCopyMenu(e, msg, clickedImageUrl, clickedFileUrl, clickedFileName) {
      const myName = (this.auth && this.auth.name) || (this.auth && this.auth.phone) || '我'
      this.copyMenu = { open: true, x: e.clientX, y: e.clientY, text: msg.text || '', name: msg.name || (msg.side === 'right' ? myName : ''), messageId: msg.id, seq: msg.seq || 0, kind: msg.kind || 'text', files: msg.files || null, fileName: msg.fileName || '', imageUrl: msg.imageUrl || '', fileUrl: msg.fileUrl || '', mimeType: msg.mimeType || '', fileSizeStr: msg.fileSizeStr || '', clickedImageUrl: clickedImageUrl || '', clickedFileUrl: clickedFileUrl || '', clickedFileName: clickedFileName || '' }
      const close = (ev) => {
        if (!ev.target.closest('.agent-copy-menu')) {
          this.copyMenu.open = false
          document.removeEventListener('click', close)
        }
      }
      setTimeout(() => document.addEventListener('click', close), 0)
    },
    autoCaptionResize() {
      const ta = this.$refs.captionInput
      if (!ta) return
      ta.style.height = 'auto'
      ta.style.height = Math.min(ta.scrollHeight, 120) + 'px'
    },
    onCaptionKeydown(e) {
      if (e.isComposing || e.keyCode === 229) return
      if (e.key === 'Escape') return
      const hk = this.$store.state.hotkeySettings || { send: { key: 'Enter' }, newline: { ctrl: true, key: 'Enter' } }
      if (this._matchHotkey(e, hk.send)) {
        e.preventDefault()
        this.sendAttachment()
      } else if (this._matchHotkey(e, hk.newline)) {
        e.preventDefault()
        const ta = e.target
        ta.setRangeText('\n', ta.selectionStart, ta.selectionEnd, 'end')
        this.attachCaption = ta.value
        this.$nextTick(() => { ta.style.height = 'auto'; ta.style.height = Math.min(ta.scrollHeight, 120) + 'px'; ta.scrollTop = ta.scrollHeight })
      } else if (e.key === 'Enter') {
        e.preventDefault()
      }
    },
    imgPlaceholderStyle(msg) {
      if (!msg.imageWidth || !msg.imageHeight) return {}
      let w = msg.imageWidth, h = msg.imageHeight
      if (w > 260) { h = h * 260 / w; w = 260 }
      if (h > 300) { w = w * 300 / h; h = 300 }
      return { width: Math.round(w) + 'px', height: Math.round(h) + 'px' }
    },
    onImageLoad() {
      const el = this.$refs.canvas
      if (!el) return
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight
      if (dist < 300) el.scrollTop = el.scrollHeight
    },
    async scrollToLatest() {
      const convId = this.currentConversationId
      const topicId = this.$store.state.currentTopicKey || undefined
      // 跳转窗口下：直接重拉最新一页覆盖 raw 数组（对齐 Telegram"切换 view"行为）
      if (convId && this.$store.state.conversationHasMoreAfter[convId]) {
        await this.$store.dispatch('fetchConversationMessages', { conversationId: convId, topicId })
      }
      this._userScrolledUp = false
      this.showScrollBtn = false
      const el = this.$refs.canvas
      if (el) this.$nextTick(() => { el.scrollTop = el.scrollHeight })
    },
    doReply() {
      this.SET_TELEGRAM_PENDING_REPLY({
        name: this.copyMenu.name,
        text: this.copyMenu.text,
        messageId: this.copyMenu.messageId,
        kind: this.copyMenu.kind,
        files: this.copyMenu.files,
        fileName: this.copyMenu.fileName,
        imageUrl: this.copyMenu.imageUrl
      })
      this.copyMenu.open = false
      this.$nextTick(() => this.focusTextarea())
    },
    clearReply() { this.SET_TELEGRAM_PENDING_REPLY(null) },
    enterMultiSelectAgent() {
      const cm = this.copyMenu
      this.copyMenu.open = false
      this.$store.commit('ENTER_MULTI_SELECT', { id: cm.messageId, seq: cm.seq || 0, name: cm.name || '', text: cm.text || '', kind: cm.kind || 'text', fileName: cm.fileName || '', imageUrl: cm.imageUrl || '', fileUrl: cm.fileUrl || '', mimeType: cm.mimeType || '', fileSizeStr: cm.fileSizeStr || '' })
    },
    isMultiSelectedAgent(id) {
      return this.multiSelectedMessages.some(m => m.id === id)
    },
    toggleMultiSelectAgent(msg) {
      this.$store.commit('TOGGLE_MULTI_SELECT_MSG', { id: msg.id, seq: msg.seq || 0, name: msg.name || '', text: msg.text || '', kind: msg.kind || 'text', fileName: msg.fileName || '', imageUrl: msg.imageUrl || '', fileUrl: msg.fileUrl || '', mimeType: msg.mimeType || '', fileSizeStr: msg.fileSizeStr || '', imageWidth: msg.imageWidth || 0, imageHeight: msg.imageHeight || 0, fileSize: msg.fileSize || 0 })
    },
    exitMultiSelectAgent() { this.$store.commit('EXIT_MULTI_SELECT') },
    multiForwardEachAgent() {
      if (!this.multiSelectedMessages.length) return
      const msgs = [...this.multiSelectedMessages].sort((a, b) => (a.seq || 0) - (b.seq || 0))
      const text = msgs.map(m => { const l = m.kind === 'image' ? '[图片]' : m.kind === 'file' ? '[文件]' : (m.text || ''); return (m.name || '未知') + '：' + l }).join('\n')
      this.$store.commit('SET_FORWARD_MODAL', { open: true, senderName: '多条消息', text, kind: 'multi-forward-each', imageUrl: '', fileName: '', files: null, _messages: msgs.map(m => ({ ...m })) })
      this.$store.commit('EXIT_MULTI_SELECT')
    },
    multiForwardMergeAgent() {
      if (!this.multiSelectedMessages.length) return
      const msgs = [...this.multiSelectedMessages].sort((a, b) => (a.seq || 0) - (b.seq || 0))
      const text = msgs.map(m => { const l = m.kind === 'image' ? '[图片]' : m.kind === 'file' ? '[文件]' : (m.text || ''); return (m.name || '未知') + '：' + l }).join('\n')
      this.$store.commit('SET_FORWARD_MODAL', { open: true, senderName: '合并转发', text, kind: 'multi-forward-merge', imageUrl: '', fileName: '', files: null, _messages: msgs.map(m => ({ ...m })) })
      this.$store.commit('EXIT_MULTI_SELECT')
    },
    doForwardFromAgent() {
      const cm = this.copyMenu
      this.copyMenu.open = false
      this.$store.commit('SET_FORWARD_MODAL', {
        open: true,
        senderName: cm.name || '未知',
        text: cm.text || '',
        kind: cm.kind || 'text',
        imageUrl: cm.imageUrl || '',
        fileUrl: cm.fileUrl || '',
        fileName: cm.fileName || '',
        mimeType: cm.mimeType || '',
        files: cm.files || null,
        imageWidth: cm.imageWidth || 0,
        imageHeight: cm.imageHeight || 0,
        fileSize: cm.fileSize || 0,
        fileSizeStr: cm.fileSizeStr || ''
      })
    },
    doCopyText() {
      const text = this.copyMenu.text
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => this._fallbackCopy(text))
      } else {
        this._fallbackCopy(text)
      }
      this.copyMenu.open = false
    },
    async doCopyImage() {
      const url = this.copyMenu.clickedImageUrl
      this.copyMenu.open = false
      if (!url) return
      let blob = null
      try {
        const resp = await fetch(url, { mode: 'cors', credentials: 'omit' })
        if (!resp.ok) throw new Error('HTTP ' + resp.status)
        blob = await resp.blob()
      } catch (e) {
        console.warn('[doCopyImage] fetch failed:', e)
      }
      // 路径 A：async clipboard（仅 HTTPS / 安全上下文）
      if (blob && window.isSecureContext && window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
        try {
          const pngBlob = blob.type === 'image/png' ? blob : await this._imageBlobToPng(blob)
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })])
          console.log('[doCopyImage] async clipboard OK')
          return
        } catch (e) {
          console.warn('[doCopyImage] async clipboard write failed:', e)
        }
      }
      // 路径 B：contenteditable + 已加载渲染的 <img> + selectNode + execCommand
      if (blob) {
        try {
          const ok = await this._copyImgAsBitmap(blob)
          if (ok) { console.log('[doCopyImage] bitmap-copy OK'); return }
        } catch (e) {
          console.warn('[doCopyImage] bitmap-copy failed:', e)
        }
        // 路径 C：copy 事件 + HTML（web 应用 fallback）
        try {
          const dataUrl = await this._blobToDataUrl(blob)
          const ok = this._copyHtmlViaCopyEvent(`<img src="${dataUrl}">`, '[图片]')
          if (ok) { console.log('[doCopyImage] html-copy OK (web app only)'); return }
        } catch (e) {
          console.warn('[doCopyImage] html-copy failed:', e)
        }
      }
      // 路径 D：兜底复制 URL 文本
      const okText = this._copyHtmlViaCopyEvent(null, url)
      if (!okText) console.warn('[doCopyImage] all paths failed')
    },
    async doDownloadFile() {
      const url = this.copyMenu.clickedFileUrl
      const name = this.copyMenu.clickedFileName || 'download'
      this.copyMenu.open = false
      if (!url) return
      try {
        const res = await fetch(url)
        if (!res.ok) throw new Error('HTTP ' + res.status)
        const blob = await res.blob()
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000)
      } catch (e) {
        console.warn('[doDownloadFile] fetch failed, fallback to open:', e)
        window.open(url, '_blank')
      }
    },
    _copyImgAsBitmap(blob) {
      return new Promise((resolve) => {
        const blobUrl = URL.createObjectURL(blob)
        const div = document.createElement('div')
        div.contentEditable = 'true'
        div.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;overflow:hidden;opacity:0.01;pointer-events:none;z-index:-9999;background:#fff'
        const img = document.createElement('img')
        img.style.cssText = 'display:block;'
        let settled = false
        const finish = (ok) => {
          if (settled) return
          settled = true
          URL.revokeObjectURL(blobUrl)
          try { document.body.removeChild(div) } catch (_) {}
          resolve(ok)
        }
        img.onload = () => {
          requestAnimationFrame(() => requestAnimationFrame(() => {
            try {
              div.focus()
              const sel = window.getSelection()
              const range = document.createRange()
              range.selectNode(img)
              sel.removeAllRanges()
              sel.addRange(range)
              let ok = false
              try { ok = document.execCommand('copy') } catch (e) { console.warn('[bitmap-copy] execCommand threw:', e) }
              sel.removeAllRanges()
              finish(ok)
            } catch (e) {
              console.warn('[bitmap-copy] select/copy threw:', e)
              finish(false)
            }
          }))
        }
        img.onerror = (e) => { console.warn('[bitmap-copy] img load failed', e); finish(false) }
        img.src = blobUrl
        div.appendChild(img)
        document.body.appendChild(div)
        setTimeout(() => finish(false), 8000)
      })
    },
    _imageBlobToPng(blob) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        const objUrl = URL.createObjectURL(blob)
        img.onload = () => {
          const c = document.createElement('canvas')
          c.width = img.naturalWidth
          c.height = img.naturalHeight
          c.getContext('2d').drawImage(img, 0, 0)
          c.toBlob(b => { URL.revokeObjectURL(objUrl); b ? resolve(b) : reject(new Error('toBlob failed')) }, 'image/png')
        }
        img.onerror = () => { URL.revokeObjectURL(objUrl); reject(new Error('image load failed')) }
        img.src = objUrl
      })
    },
    _blobToDataUrl(blob) {
      return new Promise((resolve, reject) => {
        const r = new FileReader()
        r.onload = () => resolve(r.result)
        r.onerror = () => reject(r.error || new Error('FileReader failed'))
        r.readAsDataURL(blob)
      })
    },
    _copyHtmlViaCopyEvent(html, plainText) {
      const ta = document.createElement('textarea')
      ta.value = plainText || ' '
      ta.setAttribute('readonly', '')
      ta.style.cssText = 'position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:0;opacity:0;'
      document.body.appendChild(ta)
      const handler = (e) => {
        try {
          e.preventDefault()
          if (html) e.clipboardData.setData('text/html', html)
          e.clipboardData.setData('text/plain', plainText || '')
        } catch (err) { console.warn('[clipboardData.setData] failed:', err) }
      }
      document.addEventListener('copy', handler, { once: true, capture: true })
      ta.focus()
      ta.select()
      let ok = false
      try { ok = document.execCommand('copy') } catch (e) { console.warn('[execCommand copy] threw:', e) }
      document.removeEventListener('copy', handler, { capture: true })
      document.body.removeChild(ta)
      return ok
    },
    _fallbackCopy(text) {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.cssText = 'position:fixed;left:-9999px'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    },
    openCmdPicker(e) {
      // 已选技能时不允许打开命令
      if (this.selectedSkills && this.selectedSkills.length > 0) return
      e.stopPropagation()
      this.$store.commit('SET_SKILL_PICKER_OPEN', false)
      this.$store.commit('SET_MODEL_PICKER_OPEN', false)
      this.cmdTriggerRect = e.currentTarget.getBoundingClientRect()
      this.cmdPickerOpen = !this.cmdPickerOpen
    },
    insertCommand(cmd) {
      this.CLEAR_SKILLS()
      this.cmdPickerOpen = false
      // 直接发送命令，不经过输入框
      const convId = this.currentConversationId
      if (convId) {
        this.sendConversationMessage({ conversationId: convId, text: cmd, skipOptimistic: false })
      }
    },
    toggleVoice() {
      if (this.voiceActive) {
        stopSpeechRecognition()
        this.voiceActive = false
        if (this.voiceText) {
          this.SET_COMPOSER_INPUT({ context: this.composerContext, value: this._voiceBaseText + this.voiceText })
        }
        this.voiceText = ''; this.voiceError = ''
        return
      }
      // 检查是否支持
      const reason = getSpeechUnsupportedReason()
      if (reason) {
        this.voiceActive = true; this.voiceText = ''; this.voiceError = reason
        return
      }
      this.voiceActive = true
      this.voiceText = ''; this.voiceError = ''
      this._voiceBaseText = this.inputValue || ''
      startSpeechRecognition(
        (text) => { this.voiceText = text },
        () => {
          if (this.voiceText) {
            this.SET_COMPOSER_INPUT({ context: this.composerContext, value: this._voiceBaseText + this.voiceText })
          }
          this.voiceActive = false
          this.voiceText = ''; this.voiceError = ''
        },
        (err) => { this.voiceError = err || '语音识别失败'; this.voiceText = '' }
      )
    },
    openSkillPicker(e) {
      if (this.selectedCommand || this.isCommandMode) return
      if (e) e.stopPropagation()
      this.cmdPickerOpen = false
      this.$store.commit('SET_MODEL_PICKER_OPEN', false)
      const rect = e && e.currentTarget ? e.currentTarget.getBoundingClientRect() : null
      this.$store.commit('SET_SKILL_PICKER_OPEN', { open: true, rect })
    },
    openModelPicker(e) { e.stopPropagation(); this.cmdPickerOpen = false; this.$store.commit('SET_SKILL_PICKER_OPEN', false); this.$store.commit('SET_MODEL_PICKER_OPEN', { open: true, rect: e.currentTarget.getBoundingClientRect() }) },
    openDepthPicker(e) { e.stopPropagation(); this.SET_THINKING_DEPTH_PICKER_OPEN({ open: true, rect: e.currentTarget.getBoundingClientRect() }) },
    removeSkill(id) { this.CLEAR_SKILLS(id) },
    // skill.icon 后端要么 URL（http/https/data/blob/相对路径 /），要么 emoji/单字
    isUrlIcon(s) { return typeof s === 'string' && /^(https?:|data:|blob:|\/)/.test(s) },
    onIconError(e) { try { e.target.style.display = 'none' } catch (_) {} },
    clearAllSkills() { this.CLEAR_SKILLS() },
    toggleWorkspace() { this.$store.commit('SET_WORKSPACE_OPEN', !this.$store.state.workspaceOpen) },
    toggleChatSearch() {
      const next = !this.$store.state.chatSearchOpen
      this.$store.commit('SET_CHAT_SEARCH_OPEN', next)
      if (next && !this.$store.state.workspaceOpen) {
        this.$store.commit('SET_WORKSPACE_OPEN', true)
      }
    },
    async jumpToQuote(msg) {
      const reply = msg.replyTo
      if (!reply || !reply.id) return
      const convId = this.currentConversationId
      if (!convId) return
      const topicId = this.$store.state.currentTopicKey || undefined
      const found = await this.$store.dispatch('jumpToMessage', { conversationId: convId, topicId, messageId: reply.id })
      if (!found) return
      await this.$nextTick()
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))
      const el = document.getElementById('msg-' + reply.id)
      if (!el) return
      el.scrollIntoView({ block: 'center' })
      el.classList.add('highlight-flash')
      setTimeout(() => el.classList.remove('highlight-flash'), 2000)
    },
    openTopicDialog() { this.$store.commit('SET_GROUP_DIALOG', { open: true, mode: 'agent-topic', sessionKey: this.currentSessionKey }) },
    openDetail(e) {
      const rect = e.currentTarget.getBoundingClientRect()
      this.$store.dispatch('openAgentDetailPopover', { x: Math.max(0, rect.right - 300), y: rect.bottom + 8 })
    },
    _onQuickTask(e) {
      // info-card 文件卡 → 右侧工作区预览（复用 openInWorkspace）
      const icFile = e.target.closest('a.ic-file')
      if (icFile && icFile.getAttribute('href')) {
        e.preventDefault()
        const url = icFile.getAttribute('href')
        const titleEl = icFile.querySelector('.telegram-file-title')
        const metaEl = icFile.querySelector('.telegram-file-meta')
        const fileName = (titleEl && titleEl.textContent) || '文件'
        const meta = (metaEl && metaEl.textContent) || ''
        const ext = (fileName.split('.').pop() || '').toLowerCase()
        const kind = ['jpg','jpeg','png','gif','webp','svg','bmp','ico'].includes(ext) ? 'image'
          : ['mp4','webm','mov','avi','mkv'].includes(ext) ? 'video'
          : ['mp3','wav','ogg','aac','flac','m4a'].includes(ext) ? 'audio' : 'file'
        this.openInWorkspace({ kind, fileUrl: url, imageUrl: url, fileName, mimeType: '', ext, fileSizeStr: meta })
        // 图片：和对话区一样，连着全屏预览一起弹出
        if (kind === 'image') {
          this.$store.commit('SET_FULLSCREEN_PREVIEW', {
            id: 'chat-file-preview', previewMode: 'file', title: fileName, meta,
            url, kind: 'image', fileName, mimeType: 'image/*', fileSizeStr: meta, ext
          })
        }
        return
      }
      // 同时处理 quick-tasks 卡片(.qt-card) 与 info-card 行动按钮(.ic-action)
      const btn = e.target.closest('.qt-card, .ic-action')
      if (!btn) return
      // 技能更新按钮：带 skillId → 调"为 Agent 安装 Skill"接口（带会话/话题 id，更新成功后端自动推"技能更新成功"通知）
      if (btn.dataset.skillId) {
        if (btn.disabled) return
        const agentId = this.currentAgentId
        if (!agentId) return
        const skillId = btn.dataset.skillId
        const orig = btn.innerHTML
        btn.disabled = true
        btn.innerHTML = '更新中…'
        api.installAgentSkill(agentId, {
          skillId,
          conversationId: this.currentConversationId,
          topicId: this.currentTopicKey || ''
        }).then(function () {
          btn.innerHTML = '更新请求已提交'
        }).catch(function (err) {
          btn.disabled = false
          btn.innerHTML = orig
          console.warn('[skill-install] 失败', err)
        })
        return
      }
      const action = btn.dataset.qtAction || ''
      if (!action) return
      if (btn.classList.contains('qt-card')) {
        const row = btn.closest('.qt-row')
        if (row) row.querySelectorAll('.qt-card').forEach(function (c) { c.classList.remove('qt-selected') })
        btn.classList.add('qt-selected')
      }
      const convId = this.currentConversationId
      if (convId) this.sendConversationMessage({ conversationId: convId, text: action })
    },
    renderMd(text) { return renderMarkdown(text) },
    sysClass(text) {
      if (!text) return ''
      if (/智能体|Agent/.test(text)) return 'sys-agent'
      if (/关闭了话题/.test(text)) return 'sys-topic-close'
      if (/将话题名称修改为|话题/.test(text)) return 'sys-topic'
      if (/离开|退出|移出了/.test(text)) return 'sys-leave'
      if (/加入|邀请/.test(text)) return 'sys-join'
      return ''
    },
    sysHighlight(text) {
      if (!text) return ''
      const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return escaped
        .replace(/^(.+?)\s+(邀请)\s+(.+?)\s+(加入了群聊)$/, '<span class="sys-name">$1</span> $2 <span class="sys-name">$3</span> $4')
        .replace(/^(.+?)\s+(离开了群聊)$/, '<span class="sys-name">$1</span> $2')
        .replace(/^(.+?)\s+(将)\s+(.+?)\s+(移出了群聊)$/, '<span class="sys-name">$1</span> $2 <span class="sys-name">$3</span> $4')
        .replace(/^(.+?)\s+(添加了智能体「)(.+?)(」.*)$/, '<span class="sys-name">$1</span> $2<span class="sys-name">$3</span>$4')
        .replace(/^(.+?)\s+(将智能体「)(.+?)(」.*)$/, '<span class="sys-name">$1</span> $2<span class="sys-name">$3</span>$4')
        .replace(/^(.+?)\s+(将话题名称修改为)\s+(.+)$/, '<span class="sys-name">$1</span> $2 <span class="sys-name">$3</span>')
        .replace(/^(.+?)\s+(关闭了话题)\s+(.+)$/, '<span class="sys-name">$1</span> $2 <span class="sys-name">$3</span>')
    },
    linkify(text) { return linkifyText(text) },
    renderUserMsg(text, msg) {
      if (!text) return ''
      // 转发消息卡片
      if (text.startsWith('【转发消息】')) {
        const fwdMatch = text.match(/^【转发消息】来自\s*(.+?)：([\s\S]*)/)
        if (fwdMatch) {
          const fwdFrom = fwdMatch[1].replace(/&/g, '&amp;').replace(/</g, '&lt;')
          let fwdBody = fwdMatch[2] || ''
          let fwdComment = ''
          const ci = fwdBody.indexOf('\n留言：')
          if (ci >= 0) { fwdComment = fwdBody.slice(ci + 4).replace(/&/g, '&amp;').replace(/</g, '&lt;'); fwdBody = fwdBody.slice(0, ci) }
          const bodyEsc = fwdBody.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          const bodyLinks = bodyEsc.replace(/(https?:\/\/[^\s<>"']+)/g, u => '<a href="' + u + '" target="_blank" rel="noopener noreferrer" class="telegram-link">' + u + '</a>')
          let html = '<div class="fwd-card"><div class="fwd-card-head"><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 14l5-5-5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 9H9a7 7 0 0 0-7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg> 转发消息</div><div class="fwd-card-from">来自 ' + fwdFrom + '</div><div class="fwd-card-body">' + bodyLinks + '</div></div>'
          if (fwdComment) html += '<div class="fwd-comment">' + fwdComment + '</div>'
          return html
        }
      }
      // 带文件的转发标签
      if (text.startsWith('转发自 ')) {
        let fwdText = text, fwdCommentText = ''
        const ci = text.indexOf('\n留言：')
        if (ci >= 0) { fwdCommentText = text.slice(ci + 4); fwdText = text.slice(0, ci) }
        const fwdMatch = fwdText.match(/^(转发自\s+\S+?)(?:[：:](.*))?$/s)
        if (fwdMatch) {
          const label = fwdMatch[1].replace(/&/g, '&amp;').replace(/</g, '&lt;')
          const rest = (fwdMatch[2] || '').trim()
          let bodyHtml = ''
          if (rest) {
            const esc = rest.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            bodyHtml = '<div style="margin-top:4px">' + esc.replace(/(https?:\/\/[^\s<>"']+)/g, u => '<a href="' + u + '" target="_blank" class="telegram-link">' + u + '</a>') + '</div>'
          }
          let commentHtml = fwdCommentText ? '<div class="fwd-comment">' + fwdCommentText.replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</div>' : ''
          return '<div class="fwd-file-label"><svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M15 14l5-5-5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 9H9a7 7 0 0 0-7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg> ' + label + '</div>' + bodyHtml + commentHtml
        }
      }
      // 合并转发卡片
      if (text.startsWith('【合并转发】')) {
        const lines = text.replace(/^【合并转发】\n?/, '').split('\n').filter(l => l.trim())
        const linesHtml = lines.map(l => {
          let lineHtml = '', rest = l
          const nameMatch = rest.match(/^(.+?)[：:]/)
          let nameHtml = ''
          if (nameMatch) { nameHtml = '<div class="fwd-merge-name">' + nameMatch[1].replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</div>'; rest = rest.slice(nameMatch[0].length) }
          const imgMatch = rest.match(/^\[img:([^\]]+)\]/)
          let imgHtml = ''
          if (imgMatch) { imgHtml = '<img class="fwd-merge-img" src="' + imgMatch[1] + '" loading="lazy" />'; rest = rest.slice(imgMatch[0].length) }
          const fileMatch = rest.match(/^\[file:([^:]*):([^:]*):([^\]]+)\]/) || rest.match(/^\[file:([^:]*):([^\]]+)\]/)
          let fileHtml = ''
          if (fileMatch) {
            const fName = fileMatch[1].replace(/</g, '&lt;'), fUrl = fileMatch.length === 4 ? fileMatch[3] : fileMatch[2], fSize = fileMatch.length === 4 ? fileMatch[2] : ''
            const fExt = (fName.split('.').pop() || '').toUpperCase()
            const ic = { PDF:'tg-icon-pdf', DOC:'tg-icon-doc', DOCX:'tg-icon-doc', XLS:'tg-icon-xls', XLSX:'tg-icon-xls', PPT:'tg-icon-ppt', PPTX:'tg-icon-ppt', ZIP:'tg-icon-zip', RAR:'tg-icon-zip', TXT:'tg-icon-txt', MD:'tg-icon-txt', HTML:'tg-icon-html' }[fExt] || 'tg-icon-default'
            const metaH = fSize ? '<div class="telegram-file-meta">' + fSize + '</div>' : ''
            fileHtml = '<a class="fwd-merge-file" href="' + fUrl + '" data-file-url="' + fUrl + '" data-file-name="' + fName + '"><div class="telegram-file-top"><div class="telegram-file-icon ' + ic + '">' + (fExt || '文') + '</div><div class="telegram-file-copy"><div class="telegram-file-title">' + fName + '</div>' + metaH + '</div><svg class="telegram-file-open-icon" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7v10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></div></a>'
            rest = rest.slice(fileMatch[0].length)
          }
          let textHtml = ''
          if (rest.trim()) { const esc = rest.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); textHtml = '<div class="fwd-merge-text">' + esc.replace(/(https?:\/\/[^\s<>"']+)/g, u => '<a href="' + u + '" target="_blank" class="telegram-link">' + u + '</a>') + '</div>' }
          return '<div class="fwd-merge-line">' + nameHtml + imgHtml + fileHtml + textHtml + '</div>'
        }).join('')
        return '<div class="fwd-card"><div class="fwd-card-head"><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/></svg> 合并转发</div>' + linesHtml + '</div>'
      }
      let html = ''
      const { cmd, rest, prefix } = this.parseCmd(text)
      if (cmd) {
        const prefixHtml = prefix ? linkifyText(prefix) + ' ' : ''
        const restHtml = rest ? ' ' + linkifyText(rest) : ''
        html = `${prefixHtml}<span class="bubble-cmd-tag">${cmd}</span>${restHtml}`
      } else {
        html = linkifyText(text)
      }
      // 技能标签内联显示
      if (msg && msg.skills && msg.skills.length) {
        const skillsHtml = msg.skills.map(s => `<span class="bubble-skill-tag">${s.name || s.title || ''}</span>`).join(' ')
        html = skillsHtml + ' ' + html
      }
      return html
    },
    retryMessage(msg) {
      const convId = this.currentConversationId
      if (!convId || !msg) return
      this.resendMessage({ conversationId: convId, message: msg })
    },
    // ── avatar helpers ──
    _avatarSolid(rgb) { return solidFromColor(rgb) },
    msgAvatarStyle(msg) {
      if (msg.side === 'right') {
        if (this.auth && this.auth.avatarUrl) return { background: `center / cover no-repeat url(${this.auth.avatarUrl})` }
        const bg = solidFromColor(this.auth && this.auth.avatarColorRgb)
        return bg ? { background: bg } : {}
      }
      if (msg.avatarImage) return { background: `center / cover no-repeat url(${msg.avatarImage})` }
      const bg = solidFromColor(msg.avatarColorRgb)
      return bg ? { background: bg } : {}
    },
    msgAvatarText(msg) {
      if (msg.side === 'right') {
        if (this.auth && this.auth.avatarUrl) return ''
        return (this.auth && this.auth.name) ? this.auth.name.slice(0, 1) : 'Me'
      }
      if (msg.avatarImage) return ''
      return msg.avatarText || (msg.name || '?').slice(0, 1)
    },
    // ── file helpers ──
    openInWorkspace(msg) {
      const ext = msg.ext || (msg.fileName || '').split('.').pop().toLowerCase()
      this.$store.commit('SET_GROUP_WORKSPACE_PREVIEW', { id: 'chat-file-preview', previewMode: 'file', title: msg.fileName || '文件预览', meta: msg.fileSizeStr || '', url: msg.fileUrl || msg.imageUrl || '', kind: msg.kind, fileName: msg.fileName || '', mimeType: msg.mimeType || '', fileSizeStr: msg.fileSizeStr || '', ext })
      this.$store.commit('SET_GROUP_WORKSPACE_TAB', 'preview')
      this.$store.commit('SET_WORKSPACE_OPEN', true)
    },
    openFileItem(f, msg) {
      const ext = (f.name || '').split('.').pop().toLowerCase()
      const audioExts = ['mp3','wav','ogg','aac','flac','wma','m4a']
      const videoExts = ['mp4','webm','mkv','avi','mov']
      const imageExts = ['jpg','jpeg','png','gif','webp','svg','bmp']
      let kind = f.type
      if (!kind || kind === 'file') {
        if (f.mimeType && f.mimeType.startsWith('image/')) kind = 'image'
        else if (f.mimeType && f.mimeType.startsWith('video/')) kind = 'video'
        else if (f.mimeType && f.mimeType.startsWith('audio/')) kind = 'audio'
        else if (audioExts.includes(ext)) kind = 'audio'
        else if (videoExts.includes(ext)) kind = 'video'
        else if (imageExts.includes(ext)) kind = 'image'
        else kind = 'file'
      }
      this.openInWorkspace({ ...msg, kind, imageUrl: f.url, fileUrl: f.url, fileName: f.name, mimeType: f.mimeType || '', ext })
    },
    fileIconLabel(msg) {
      const ext = (msg.ext || (msg.fileName || '').split('.').pop()).toLowerCase()
      if (ext === 'pdf') return 'PDF'
      if (ext === 'doc' || ext === 'docx') return 'DOC'
      if (ext === 'xls' || ext === 'xlsx') return 'XLS'
      if (ext === 'ppt' || ext === 'pptx') return 'PPT'
      if (ext === 'zip' || ext === 'rar' || ext === '7z') return 'ZIP'
      if (ext === 'txt' || ext === 'md') return 'TXT'
      if (ext === 'html' || ext === 'htm') return 'HTML'
      if (msg.kind === 'audio') return '♪'
      if (msg.kind === 'video') return '▶'
      return '文'
    },
    fileIconClass(msg) {
      const ext = (msg.ext || (msg.fileName || '').split('.').pop()).toLowerCase()
      if (ext === 'pdf') return 'tg-icon-pdf'
      if (ext === 'doc' || ext === 'docx') return 'tg-icon-doc'
      if (ext === 'xls' || ext === 'xlsx') return 'tg-icon-xls'
      if (ext === 'ppt' || ext === 'pptx') return 'tg-icon-ppt'
      if (ext === 'zip' || ext === 'rar' || ext === '7z') return 'tg-icon-zip'
      if (ext === 'txt' || ext === 'md') return 'tg-icon-txt'
      if (ext === 'html' || ext === 'htm') return 'tg-icon-html'
      return 'tg-icon-default'
    },
    fileExtLabel(fileName) { return (fileName || '').split('.').pop().toUpperCase() || '文' },
    isImageFile(f) {
      if (!f) return false
      if (f.type === 'image') return true
      if (f.mimeType && f.mimeType.startsWith('image/')) return true
      const ext = (f.name || '').split('.').pop().toLowerCase()
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)
    },
    formatSize(bytes) {
      if (!bytes) return ''
      return bytes < 1024*1024 ? (bytes/1024).toFixed(1)+' KB' : (bytes/(1024*1024)).toFixed(1)+' MB'
    },
    // ── audio ──
    _getAudio(id) { const r = this.$refs['audio_' + id]; return Array.isArray(r) ? r[0] : r },
    _ensureAudioState(id) { if (!this.audioState[id]) this.$set(this.audioState, id, { playing: false, current: 0, duration: 0 }) },
    toggleAudio(id) {
      this._ensureAudioState(id); const el = this._getAudio(id); if (!el) return
      Object.keys(this.audioState).forEach(k => { if (k !== String(id) && this.audioState[k] && this.audioState[k].playing) { const o = this._getAudio(k); if (o) o.pause(); this.$set(this.audioState[k], 'playing', false) } })
      if (el.paused) { el.play(); this.$set(this.audioState[id], 'playing', true) } else { el.pause(); this.$set(this.audioState[id], 'playing', false) }
    },
    onAudioTimeUpdate(id, e) { this._ensureAudioState(id); this.$set(this.audioState[id], 'current', e.target.currentTime); this.$set(this.audioState[id], 'duration', e.target.duration || 0) },
    onAudioMeta(id, e) { this._ensureAudioState(id); this.$set(this.audioState[id], 'duration', e.target.duration || 0) },
    onAudioEnded(id) { this._ensureAudioState(id); this.$set(this.audioState[id], 'playing', false); this.$set(this.audioState[id], 'current', 0); const el = this._getAudio(id); if (el) el.currentTime = 0 },
    seekAudio(id, e) {
      this._ensureAudioState(id); const el = this._getAudio(id); if (!el) return
      const rect = e.currentTarget.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const dur = el.duration || this.audioState[id].duration || 0
      if (dur) el.currentTime = ratio * dur
    },
    audioProgress(id) { const s = this.audioState[id]; if (!s || !s.duration) return 0; return Math.min(100, (s.current / s.duration) * 100) },
    audioDisplayTime(id) {
      const s = this.audioState[id]
      const fmt = t => { if (!t || isNaN(t)) return '0:00'; return Math.floor(t/60) + ':' + String(Math.floor(t%60)).padStart(2,'0') }
      if (!s) return '0:00'
      return (s.playing || s.current > 0) ? fmt(s.current) : fmt(s.duration)
    },
    // ── 附件 ──
    _fileToAttachment(file) {
      const mimeType = file.type || '', ext = (file.name || '').split('.').pop().toLowerCase()
      let kind
      if (mimeType.startsWith('image/')) kind = 'image'
      else if (mimeType.startsWith('video/')) kind = 'video'
      else if (mimeType.startsWith('audio/')) kind = 'audio'
      else if (ext === 'md') kind = 'document'
      else if (['pdf','doc','docx','xls','xlsx','ppt','pptx'].includes(ext)) kind = 'document'
      else kind = 'file'
      const bytes = file.size
      const fileSizeStr = bytes > 0 ? (bytes < 1024*1024 ? (bytes/1024).toFixed(1)+' KB' : (bytes/(1024*1024)).toFixed(1)+' MB') : ''
      const localUrl = (kind === 'image' || kind === 'video') ? URL.createObjectURL(file) : ''
      return { file, kind, localUrl, fileName: file.name || '未命名', fileSizeStr, mimeType }
    },
    _openAttachPanel(files) {
      if (!files || !files.length) return
      const currentText = this.inputValue || ''
      if (currentText.trim() && !this.attachCaption) { this.attachCaption = currentText; this.SET_COMPOSER_INPUT({ context: this.composerContext, value: '' }); this.$nextTick(() => this.autoResize()) }
      this.pendingAttachments = [...this.pendingAttachments, ...Array.from(files).map(f => this._fileToAttachment(f))]
      this.$nextTick(() => {
        if (this.$refs.captionInput) {
          this.$refs.captionInput.focus()
          // 程序化赋值 attachCaption 不会触发 @input，需要手动撑高
          this.autoCaptionResize()
        }
      })
    },
    triggerFileInput() { const input = this.$refs.fileInput; if (!input) return; try { input.value = '' } catch(e) {}; input.click() },
    handleFileSelect(e) { const fa = e.target.files ? Array.from(e.target.files) : []; e.target.value = ''; if (fa.length) this._openAttachPanel(fa) },
    onDragLeave(e) { if (!e.currentTarget.contains(e.relatedTarget)) this.isDragOver = false },
    handleDrop(e) { this.isDragOver = false; const f = e.dataTransfer && e.dataTransfer.files; if (f && f.length) this._openAttachPanel(f) },
    handlePaste(e) {
      const items = e.clipboardData && e.clipboardData.items; if (!items) return
      const fi = Array.from(items).filter(it => it.kind === 'file'); if (!fi.length) return
      const files = fi.map(it => it.getAsFile()).filter(Boolean); if (!files.length) return
      e.preventDefault(); this._openAttachPanel(files)
    },
    removeAttachment(idx) { const a = this.pendingAttachments[idx]; if (a && a.localUrl) URL.revokeObjectURL(a.localUrl); this.pendingAttachments.splice(idx, 1) },
    fileKindLabel(att) { return (att.fileName || '').split('.').pop().toUpperCase() || att.kind.toUpperCase() || '文' },
    cancelAttachment() {
      if (this.attachCaption.trim()) this.SET_COMPOSER_INPUT({ context: this.composerContext, value: this.attachCaption })
      this.pendingAttachments.forEach(a => { if (a.localUrl) URL.revokeObjectURL(a.localUrl) })
      this.pendingAttachments = []; this.attachCaption = ''
      this.$nextTick(() => this.autoResize())
    },
    async sendAttachment() {
      if (!this.pendingAttachments.length) return
      // 发送前先滚动到底部（与 telegram 私聊/群聊一致）
      this._userScrolledUp = false
      this.showScrollBtn = false
      const _canvas = this.$refs.canvas
      if (_canvas) this.$nextTick(() => { _canvas.scrollTop = _canvas.scrollHeight })
      const attachments = [...this.pendingAttachments], caption = this.attachCaption
      this.pendingAttachments = []; this.attachCaption = ''
      this.$nextTick(() => this.autoResize())
      const convId = this.currentConversationId
      const topicId = this.$store.getters.safeCurrentTopicKey || undefined
      const tempId = 'temp-file-' + Date.now()
      const fileReply = this.pendingReply
      const fileReplyToId = fileReply ? fileReply.messageId : undefined
      if (fileReply) this.clearReply()
      if (convId && attachments.length) {
        const now = new Date()
        const timeStr = now.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
        const first = attachments[0]
        const tempFiles = attachments.map(att => ({ type: att.kind, url: att.localUrl || '', name: att.fileName, size: att.file.size, mimeType: att.mimeType }))
        const tempMsg = {
          id: tempId,
          side: 'right', name: '', text: caption, time: timeStr,
          avatarText: 'Me', avatarColorRgb: (this.auth && this.auth.avatarColorRgb) || '',
          kind: attachments.length > 1 ? 'multi' : first.kind,
          imageUrl: first.kind === 'image' ? first.localUrl : '',
          fileUrl: first.localUrl, fileName: first.fileName,
          fileSize: first.file.size, fileSizeStr: first.fileSizeStr, mimeType: first.mimeType,
          ext: (first.fileName || '').split('.').pop().toLowerCase(),
          files: tempFiles, status: 'sending', progress: 0,
          replyTo: fileReply ? { id: fileReply.messageId, name: fileReply.name || '', text: fileReply.text || '', files: fileReply.files || null } : null
        }
        this.$store.commit('APPEND_CONVERSATION_MESSAGE', { conversationId: convId, message: tempMsg })
        // 乐观追加后再滚一次到底（确保新气泡可见）
        this.$nextTick(() => { if (_canvas) _canvas.scrollTop = _canvas.scrollHeight })
      }
      try {
        if (convId) {
          // 真实进度：各文件/分片 onProgress 按字节加权汇总（断网暂停 / 续传继续）
          const _sizes = attachments.map(a => (a.file && a.file.size) || 0)
          const _total = _sizes.reduce((a, b) => a + b, 0) || 1
          const _pct = new Array(attachments.length).fill(0)
          const _commitProgress = () => {
            let loaded = 0
            for (let i = 0; i < attachments.length; i++) loaded += (_pct[i] / 100) * _sizes[i]
            this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempId, progress: Math.min(99, Math.round(loaded / _total * 100)) })
          }
          const uploaded = await Promise.all(attachments.map(async (att, i) => {
            const onP = (p) => { _pct[i] = p; _commitProgress() }
            const result = att.kind === 'image' ? await api.uploadImage(att.file, onP) : await api.uploadFile(att.file, onP)
            const url = result.url || result.file_url || result.fileUrl || ''
            const item = { type: att.kind, name: att.fileName, url, size: att.file.size, mimeType: att.mimeType }
            if (att.kind === 'image' && att.localUrl) {
              try {
                const dim = await _getImageDimensions(att.localUrl)
                if (dim.width) { item.width = dim.width; item.height = dim.height }
              } catch {}
            }
            return item
          }))
          this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempId, progress: 99 })
          const sendResult = await api.sendMediaMessage(convId, { messageType: 'file', body: { files: uploaded, caption }, topicId, replyToId: fileReplyToId })
          const realMsgId = sendResult && (sendResult.id || sendResult.messageId) ? (sendResult.id || sendResult.messageId) : tempId
          if (realMsgId !== tempId) {
            this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempId, id: realMsgId })
          }
          const realUrl = uploaded[0] ? uploaded[0].url : ''
          if (realUrl && attachments[0].kind === 'image') await _preloadImage(realUrl)
          const realFiles = uploaded.map(u => ({ type: u.type, url: u.url, name: u.name, size: u.size, mimeType: u.mimeType }))
          this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: realMsgId, status: 'sent', progress: undefined, fileUrl: realUrl, imageUrl: attachments[0].kind === 'image' ? realUrl : '', files: realFiles })
        }
      } catch (err) {
        console.warn('[AgentStateProcessing] 文件发送失败:', err.message)
        if (convId) {
          if (this.currentConversationId === convId) {
            this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempId, status: 'failed' })
          } else {
            this.$store.commit('REMOVE_CONVERSATION_MESSAGE', { conversationId: convId, messageId: tempId })
          }
        }
      } finally { attachments.forEach(a => { if (a.localUrl) URL.revokeObjectURL(a.localUrl) }) }
    }
  },
  watch: {
    composerContext() {
      this.$nextTick(() => this.autoResize())
    },
    apiMessages(newList, oldList) {
      const newCount = (newList || []).filter(m => m.kind !== 'day').length
      const oldCount = (oldList || []).filter(m => m.kind !== 'day').length
      if (newCount > oldCount && !this._userScrolledUp) {
        this.$nextTick(() => this.smoothScrollToBottom(true))
      }
    },
    streamingMessage: {
      handler(val) {
        if (!val) {
          if (this._typewriterTimer) { clearInterval(this._typewriterTimer); this._typewriterTimer = null }
          this._streamSwitchedAway = true
        } else if (val.content) {
          var contentLen = val.content.length
          if (this.streamRenderedLen > contentLen) {
            this.streamRenderedLen = 0
          }
          if (this._streamSwitchedAway) {
            this._streamSwitchedAway = false
            this.streamRenderedLen = contentLen
          } else if (this.streamRenderedLen >= contentLen) {
            this.streamRenderedLen = contentLen
          } else if (!this._typewriterTimer) {
            this._typewriterTimer = setInterval(() => {
              var target = (this.currentStreamingMessage && this.currentStreamingMessage.content) || ''
              if (this.streamRenderedLen < target.length) {
                this.streamRenderedLen += 1
                if (!this._userScrolledUp) {
                  var el = this.$refs.canvas
                  if (el) { var dist = el.scrollHeight - el.scrollTop - el.clientHeight; if (dist < 100) el.scrollTop = el.scrollHeight }
                }
              } else {
                clearInterval(this._typewriterTimer)
                this._typewriterTimer = null
              }
            }, 20)
          }
        }
        // 用户手动滚上去看历史时不打断
        if (this._userScrolledUp) return
        this.$nextTick(() => this.smoothScrollToBottom(false))
      },
      deep: true
    },
    currentSessionKey(newKey, oldKey) {
      // 离开前保存旧会话的滚动位置
      if (oldKey) this._saveScrollPos()
      // 进入新会话：恢复滚动位置
      this.$nextTick(() => { this._restoreScrollPos(); this.focusTextarea() })
      // 切会话时强制把流式显示长度对齐到当前 content 真实长度，避免切回时从头打字重放
      this._syncStreamRendered()
    },
    currentTopicKey(newKey, oldKey) {
      if (oldKey) this._saveScrollPos()
      this.$nextTick(() => { this._restoreScrollPos(); this.focusTextarea() })
      this._syncStreamRendered()
    },
  },
  mounted() {
    this.autoResize()
    this._userScrolledUp = false
    this._scrollRaf = null
    this._lastUserGesture = 0
    this._markUserGesture = () => { this._lastUserGesture = Date.now() }
    if (this.$el) this.$el.addEventListener('paste', this.handlePaste)
    if (this.$el) this.$el.addEventListener('click', this._onQuickTask)
    const canvas = this.$refs.canvas
    if (canvas) {
      canvas.addEventListener('scroll', this.onCanvasScroll, { passive: true })
      canvas.addEventListener('wheel', this._markUserGesture, { passive: true })
      canvas.addEventListener('touchmove', this._markUserGesture, { passive: true })
      canvas.addEventListener('keydown', this._markUserGesture)
    }
    this.$nextTick(() => { this._restoreScrollPos(); this.focusTextarea() })
    // 重建时立即把 streamRenderedLen 对齐到当前 streaming content 长度（跳过打字）
    // 修复：跨会话切换且经过非 agent tab 时，<section v-if> 销毁重建组件，data 重置为 0
    // 第一个 chunk 来时打字机会从 0 开始，看起来像「从头重放」
    this._syncStreamRendered()
  },
  beforeDestroy() {
    this._saveScrollPos()
    if (this.$el) this.$el.removeEventListener('paste', this.handlePaste)
    if (this.$el) this.$el.removeEventListener('click', this._onQuickTask)
    const canvas = this.$refs.canvas
    if (canvas) {
      canvas.removeEventListener('scroll', this.onCanvasScroll)
      if (this._markUserGesture) {
        canvas.removeEventListener('wheel', this._markUserGesture)
        canvas.removeEventListener('touchmove', this._markUserGesture)
        canvas.removeEventListener('keydown', this._markUserGesture)
      }
    }
    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf)
  }
}
</script>
